import { useEffect, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { UpsellMeeting } from 'modules/upsell/components/UpsellMeeting'

const WEBPAGE_MEETINGS_API_URL = 'https://factorialhr.com/api/hubspot/meetings'
const HUBSPOT_MEETINGS_URL = 'https://meetings.hubspot.com'
const LARGE_COMPANY_SIZE = 1000

const COUNTRY_SIGNUP_URLS: Record<string, string> = {
  es: 'https://factorial.es',
  it: 'https://factorial.it',
  fr: 'https://factorialhr.fr',
  de: 'https://factorial.de',
  gb: 'https://factorialhr.co.uk',
  uk: 'https://factorialhr.co.uk',
  pt: 'https://factorialhr.pt',
  us: 'https://factorialhr.com',
  mx: 'https://factorialhr.mx',
  br: 'https://factorial.com.br',
  co: 'https://factorial.co',
  ar: 'https://factorial.ar',
  cl: 'https://factorial.cl',
  pl: 'https://factorial.pl',
}

const DEFAULT_SIGNUP_URL = 'https://factorialhr.com'

const getSignupUrl = (country: string | undefined): string => {
  const normalizedCountry = country?.toLowerCase()
  const baseUrl =
    (normalizedCountry && COUNTRY_SIGNUP_URLS[normalizedCountry]) || DEFAULT_SIGNUP_URL
  return `${baseUrl}/get-started`
}

const fetchSdrCalendarUrl = async (country: string): Promise<string | null> => {
  const params = new URLSearchParams({
    country,
    companySize: LARGE_COMPANY_SIZE.toString(),
  })
  const response = await fetch(`${WEBPAGE_MEETINGS_API_URL}?${params}`)
  if (!response.ok) return null

  const data = await response.json()
  if (!data.sdrName) return null

  return `${HUBSPOT_MEETINGS_URL}/${data.sdrName}`
}

type BannerVariant = 'big' | 'small'

interface BookAMeetingBannerProps {
  variant: BannerVariant
}

const BookAMeetingBanner = ({ variant }: BookAMeetingBannerProps) => {
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const [calendarUrl, setCalendarUrl] = useState<string | null>(null)
  const { company } = useCurrentSession()

  const mainLegalEntity = company?.legalEntities?.find((le) => le.main)
  const country = mainLegalEntity?.country

  useEffect(() => {
    if (variant !== 'big') return
    if (!country) return

    fetchSdrCalendarUrl(country)
      .then(setCalendarUrl)
      .catch(() => setCalendarUrl(null))
  }, [country, variant])

  const handleOpenMeetingModal = () => setIsMeetingModalOpen(true)
  const handleCloseMeetingModal = () => setIsMeetingModalOpen(false)
  const handleSignupRedirect = () => {
    window.location.href = getSignupUrl(country)
  }

  if (variant === 'big' && !calendarUrl) {
    return null
  }

  const isBigVariant = variant === 'big'
  const bannerText = isBigVariant
    ? i18n.t('onboarding.banners.book_a_meeting_big_companies')
    : i18n.t('onboarding.banners.book_a_meeting_small_companies')
  const buttonText = isBigVariant
    ? i18n.t('onboarding.banners.talk_to_an_agent')
    : i18n.t('onboarding.banners.create_account')
  const handleClick = isBigVariant ? handleOpenMeetingModal : handleSignupRedirect

  return (
    <>
      <Box
        flexDirection="row"
        padding="s8"
        width="full"
        style={{
          background: 'linear-gradient(90deg, #D14A58 16.67%, #D03590 52.08%, #D2282F 88.54%)',
        }}
        justifyContent="center"
        alignItems="center"
        gap="s12"
      >
        <Text.Normal color="grey000White" weight="normal">
          {bannerText}
        </Text.Normal>
        <Button type="primary" onClick={handleClick}>
          {buttonText}
        </Button>
      </Box>
      {isBigVariant && (
        <UpsellMeeting
          isOpen={isMeetingModalOpen}
          onClose={handleCloseMeetingModal}
          meetingUrl={calendarUrl || ''}
          trackingSource="book_a_meeting_banner"
          moduleRequested={AvailableModules.timeoff}
          requestSource="From demo generator book a meeting banner"
          skipCloseModal
          upsellRequestType="Book a meeting trial user"
        />
      )}
    </>
  )
}

export default BookAMeetingBanner
